import Phaser from "phaser";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { Cat } from "../sprites/CatSprite";
import { Level } from "../../data/LevelModel";
import { UserAttempt } from "../../data/LevelAttempt";
import { TransferSystem } from "./TransferSystem";

export interface TransferControllerOptions {
  adultMode: boolean;
  dialPrecision: number;
  categories: {
    name: string;
    spent: number;
    budgeted: number;
    necessary: boolean;
    savings: boolean;
    surplus?: boolean;
  }[];
  checkWin?: () => boolean;
  onCategoriesChanged: () => void;
  onWin: () => void;
}

/**
 * Handles donor-cat selection, kibble transfers, haptic feedback, and win-check
 * logic so individual scenes remain lightweight.
 */
export class TransferController {
  private donorCat: Cat | null = null;
  private transferSystem!: TransferSystem;

  constructor(
    private scene: Phaser.Scene,
    private levelOrNull: Level | null,
    private cats: Cat[],
    private attempt: UserAttempt | null,
    private opts: TransferControllerOptions,
  ) {}

  private get categories() {
    return this.opts.categories;
  }

  private dispatchSelection(index: number) {
    window.dispatchEvent(
      new CustomEvent("phaser-cat-clicked", {
        detail: { index, selectedIndex: index },
      }),
    );
  }

  /** Call from a cat's pointerdown handler */
  handleCatClick(clickedCat: Cat): void {
    // Ignore clicks on cats that are currently walking to the centre (balance move)
    if ((clickedCat as any).currentAnimation === "walk") {
      return;
    }

    this.triggerHaptic(ImpactStyle.Light);

    if (this.donorCat === null) {
      // No donor selected ‑ set it
      this.setDonorCat(clickedCat);
      this.dispatchSelection(this.cats.indexOf(clickedCat));
    } else if (this.donorCat === clickedCat) {
      // Same cat toggles donor off
      this.clearDonorCat();
      this.dispatchSelection(-1);
    } else {
      // Transfer action
      this.triggerHaptic(ImpactStyle.Medium);
      this.transferKibbles(clickedCat);
    }
  }

  /* ───────── internal helpers ─────────────────────────── */
  private setDonorCat(cat: Cat) {
    this.donorCat = cat;
  }
  private clearDonorCat() {
    this.donorCat = null;
  }

  private getCategoryIndexForCat(cat: Cat): number | null {
    return this.cats.indexOf(cat);
  }

  private triggerHaptic(style: ImpactStyle) {
    try {
      Haptics.impact({ style });
    } catch {
      /* ignore on web */
    }
  }

  private transferKibbles(recipient: Cat): void {
    if (!this.donorCat) return;

    const donorIdx = this.getCategoryIndexForCat(this.donorCat);
    const recipIdx = this.getCategoryIndexForCat(recipient);
    if (donorIdx === -1 || recipIdx === -1) return;

    const donorCatData = this.categories[donorIdx];
    const recipCatData = this.categories[recipIdx];

    // Calculate donor surplus (budgeted > spent) and recipient deficit (spent > budgeted)
    const donorSurplus = Math.max(
      0,
      donorCatData.budgeted - donorCatData.spent,
    );
    const recipientDeficit = Math.max(
      0,
      recipCatData.spent - recipCatData.budgeted,
    );

    // Nothing to transfer if donor has no surplus or recipient not in deficit
    if (donorSurplus === 0 || recipientDeficit === 0) return;

    // Amount to transfer is the minimum of surplus and deficit
    const amount = Math.min(donorSurplus, recipientDeficit);

    if (amount <= 0) return; // safety

    // attempt log
    if (this.attempt) {
      this.attempt.recordMove("transfer_budget", {
        from: donorCatData.name,
        to: recipCatData.name,
        amount,
        donorNewBalance: donorCatData.budgeted,
        recipientNewBalance: recipCatData.budgeted,
      });
    }

    // Prepare coin animation start/end points
    const offset = 75;
    const dMat = this.donorCat.getWorldTransformMatrix();
    const rMat = recipient.getWorldTransformMatrix();
    const start = new Phaser.Math.Vector2(dMat.tx, dMat.ty - offset);
    const end = new Phaser.Math.Vector2(rMat.tx, rMat.ty - offset);

    // Lazy-create transferSystem instance
    if (!this.transferSystem) {
      this.transferSystem = new TransferSystem(
        this.scene,
        this.cats,
        this.categories,
        () => this.opts.onCategoriesChanged(),
        "budgeted",
      );
    }

    this.transferSystem.transfer(donorIdx, recipIdx, amount, start, end);

    // If donor category still has budget left, keep selection; otherwise clear
    if (donorCatData.budgeted === donorCatData.spent) {
      this.clearDonorCat();
      this.dispatchSelection(-1);
    } else {
      // keep donor selected, nothing to dispatch
    }

    // Win check
    if (
      this.opts.checkWin
        ? this.opts.checkWin()
        : this.levelOrNull?.checkWinCondition()
    ) {
      this.opts.onWin();
    }
  }

  dispose() {
    this.donorCat = null;
  }
}
